import { useEffect, useState } from 'react';
import axios from 'axios';

interface Member {
  user_id: number;
  full_name: string;
  callsign: number;
  entry_date: string | Date;
  last_up: string | Date;
  discordID: string;
  functions?: Record<string, boolean | string>;
  certificates?: Record<string, boolean | string>;
  fw: number;
  notes: string;
  grade: string;
  adminLevel: number;
  concediu: number;
  reintegrated: boolean;
  email: string;
}

interface Punishment {
  callsign: string;
  reason: string;
  amount: number;
  hr: string;
  punishments: any;
  date: string;
  expire: string;
  status: boolean;
  pType: string; 
  fw: number;
  type: string;
}

interface Cooldown {
  callsign: number;
  type: string;
  cType: string;
  start_date: string | Date;
  days: number;
  confiscated: boolean;
  paid_status: boolean;
}

interface Token {
  email: string,
  token: string
}

const API_URL = 'http://panelpolitie.fplayt.ro:5001';

export const useData = () => {
  const [policeMembersData, setPoliceData] = useState<Member[]>([]);
  const [penitenciarMembersData, setPenitenciarData] = useState<Member[]>([]);
  const [academieMembersData, setAcademieData] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [allMembersData, setAllMembersData] = useState<Member[]>([]);

  const fetchData = async () => {
    try {
      const politieResponse = await axios.get<Member[]>(`${API_URL}/api/getPoliceData`, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
      const penitenciarResponse = await axios.get<Member[]>(`${API_URL}/api/getPenitenciarData`, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
      const academieResponse = await axios.get<Member[]>(`${API_URL}/api/getAcademieData`, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
      const policeProcessedData = politieResponse.data.map((element) => {
        const updatedElement = { ...element };

        if (element.functions && typeof element.functions === 'string') {
          try {
            updatedElement.functions = JSON.parse(element.functions);
          } catch (e) {
            console.error('Error parsing functions:', e);
          }
        }

        if (element.certificates && typeof element.certificates === 'string') {
          try {
            updatedElement.certificates = JSON.parse(element.certificates);
          } catch (e) {
            console.error('Error parsing certificates:', e);
          }
        }

        return updatedElement;
      });

      const penitenciarProcessedData = penitenciarResponse.data.map((element) => {
        const updatedElement = { ...element };

        if (element.functions && typeof element.functions === 'string') {
          try {
            updatedElement.functions = JSON.parse(element.functions);
          } catch (e) {
            console.error('Error parsing functions:', e);
          }
        }

        if (element.certificates && typeof element.certificates === 'string') {
          try {
            updatedElement.certificates = JSON.parse(element.certificates);
          } catch (e) {
            console.error('Error parsing certificates:', e);
          }
        }

        return updatedElement;
      });

      const academieProcessedData = academieResponse.data.map((element) => {
        const updatedElement = { ...element };

        if (element.functions && typeof element.functions === 'string') {
          try {
            updatedElement.functions = JSON.parse(element.functions);
          } catch (e) {
            console.error('Error parsing functions:', e);
          }
        }

        if (element.certificates && typeof element.certificates === 'string') {
          try {
            updatedElement.certificates = JSON.parse(element.certificates);
          } catch (e) {
            console.error('Error parsing certificates:', e);
          }
        }

        return updatedElement;
      });

      setPoliceData(policeProcessedData);
      setPenitenciarData(penitenciarProcessedData);
      setAcademieData(academieProcessedData);
      let tempData = policeProcessedData.concat(penitenciarProcessedData);
      setAllMembersData(tempData.concat(academieProcessedData))
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getDiscordIDType = (discord: string): any => {
    for (let index = 0; index < allMembersData.length; index++) {
      const element = allMembersData[index];
      if (element.discordID === discord) {
        if (element.grade === 'AGENT-PAZA' || element.grade === "AGENT-PAZA-SEF" || element.grade === "DIRECTOR-PENITENCIAR") {
          return 'pen';
        } else if (element.grade === "ACADEMIE") {
          return 'ac';
        } else {
          return 'pol';
        }
      }
    }
  }

  const getPoliceMemberInfo = (callsign: number, type: keyof Member): any => {
    const member = policeMembersData.find((m) => m.callsign === callsign);
    return member ? member[type] : null;
  };

  const getPenMemberInfo = (callsign: number, type: keyof Member): any => {
    const member = penitenciarMembersData.find((m) => m.callsign === callsign);
    return member ? member[type] : null;
  };

  const getPoliceDiscordMemberInfo = (dID: string, type: keyof Member): any => {
    const member = policeMembersData.find((m) => m.discordID === dID);
    return member ? member[type] : null;
  };

  const getPenDiscordMemberInfo = (dID: string, type: keyof Member): any => {
    const member = penitenciarMembersData.find((m) => m.discordID === dID);
    return member ? member[type] : null;
  };

  const getAcademieMemberInfo = (userid: number, type: keyof Member): any => {
    const member = academieMembersData.find((m) => m.user_id === userid);
    return member ? member[type] : null;
  };

  return { policeMembersData, penitenciarMembersData, academieMembersData, getDiscordIDType, allMembersData, loading, getPenDiscordMemberInfo, getAcademieMemberInfo, getPoliceDiscordMemberInfo, getPoliceMemberInfo, getPenMemberInfo, fetchData };
};


export const useTokenEmail = () => {
  const [tokenData, setData] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const response = await axios.get<Token[]>(`${API_URL}/api/tokenData`, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { tokenData, loading };
};

export const usePunishments = () => {
  const [membersPunishments, setPData] = useState<Punishment[]>([]);
  const [pLoading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const response = await axios.get<Punishment[]>(`${API_URL}/api/punishmentsData`, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
      setPData(response.data.reverse());
    } catch (error) {
      console.error('Error fetching punishments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); 

  return { membersPunishments, fetchData };
};


export const useCooldowns = () => {
  const [membersCooldowns, setPData] = useState<Cooldown[]>([]);
  const [pLoading, setLoading] = useState(true);

  const fetchCooldowns = async () => {
    try {
      const response = await axios.get<Cooldown[]>(`${API_URL}/api/cooldownsData`, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
      setPData(response.data.reverse());
    } catch (error) {
      console.error('Error fetching punishments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCooldowns();
  }, []); 

  return { membersCooldowns, fetchCooldowns };
};
